import { Injectable, inject } from '@angular/core';
import { IUser } from '@core/interfaces/user.interface';
import { Observable, map } from 'rxjs';
import { AuthService } from './auth.service';

@Injectable({
  providedIn: 'root',
})
export class PermissionService {
  private readonly authService = inject(AuthService);

  /**
   * Get current user's effective permissions
   */
  public get currentUserPermissions(): string[] {
    const currentUser = AuthService.currentDbUser;

    return currentUser?.effectivePermissions || [];
  }

  /**
   * Observable of current user's effective permissions
   */
  public get currentUserPermissions$(): Observable<string[]> {
    return this.authService.currentDbUser$.pipe(map((user: IUser | null) => user?.effectivePermissions || []));
  }

  /**
   * Check if user has a specific permission
   * @param permission - The permission to check
   * @returns true if user has the permission, false otherwise
   */
  public hasPermission(permission: string): boolean {
    const permissions = this.currentUserPermissions;

    return permissions.includes(permission);
  }

  /**
   * Check if user has a specific permission (Observable)
   * @param permission - The permission to check
   * @returns Observable<boolean>
   */
  public hasPermission$(permission: string): Observable<boolean> {
    return this.currentUserPermissions$.pipe(map((permissions: string[]) => permissions.includes(permission)));
  }

  /**
   * Check if user has ALL of the specified permissions
   * @param permissions - Array of permissions to check
   * @returns true if user has all permissions, false otherwise
   */
  public hasAllPermissions(permissions: string[]): boolean {
    const userPermissions = this.currentUserPermissions;

    return permissions.every((permission) => userPermissions.includes(permission));
  }

  /**
   * Check if user has ALL of the specified permissions (Observable)
   * @param permissions - Array of permissions to check
   * @returns Observable<boolean>
   */
  public hasAllPermissions$(permissions: string[]): Observable<boolean> {
    return this.currentUserPermissions$.pipe(
      map((userPermissions: string[]) => permissions.every((permission) => userPermissions.includes(permission))),
    );
  }

  /**
   * Check if user has ANY of the specified permissions
   * @param permissions - Array of permissions to check
   * @returns true if user has at least one permission, false otherwise
   */
  public hasAnyPermission(permissions: string[]): boolean {
    const userPermissions = this.currentUserPermissions;

    return permissions.some((permission) => userPermissions.includes(permission));
  }

  /**
   * Check if user has ANY of the specified permissions (Observable)
   * @param permissions - Array of permissions to check
   * @returns Observable<boolean>
   */
  public hasAnyPermission$(permissions: string[]): Observable<boolean> {
    return this.currentUserPermissions$.pipe(
      map((userPermissions: string[]) => permissions.some((permission) => userPermissions.includes(permission))),
    );
  }

  /**
   * Check if user has a specific role
   * @param roleId - The role ID to check
   * @returns true if user has the role, false otherwise
   */
  public hasRole(roleId: string): boolean {
    const currentUser = AuthService.currentDbUser;

    return currentUser?.roles?.some((role) => role.id === roleId) || false;
  }

  /**
   * Check if user has a specific role (Observable)
   * @param roleId - The role ID to check
   * @returns Observable<boolean>
   */
  public hasRole$(roleId: string): Observable<boolean> {
    return this.authService.currentDbUser$.pipe(
      map((user: IUser | null) => user?.roles?.some((role) => role.id === roleId) || false),
    );
  }

  /**
   * Check if user has ANY of the specified roles
   * @param roleIds - Array of role IDs to check
   * @returns true if user has at least one role, false otherwise
   */
  public hasAnyRole(roleIds: string[]): boolean {
    const currentUser = AuthService.currentDbUser;

    return roleIds.some((roleId) => currentUser?.roles?.some((role) => role.id === roleId) || false);
  }

  /**
   * Check if user has ANY of the specified roles (Observable)
   * @param roleIds - Array of role IDs to check
   * @returns Observable<boolean>
   */
  public hasAnyRole$(roleIds: string[]): Observable<boolean> {
    return this.authService.currentDbUser$.pipe(
      map((user: IUser | null) => roleIds.some((roleId) => user?.roles?.some((role) => role.id === roleId) || false)),
    );
  }

  /**
   * Get all user's role IDs
   * @returns Array of role IDs
   */
  public getCurrentUserRoles(): string[] {
    const currentUser = AuthService.currentDbUser;

    return currentUser?.roles?.map((role) => role.id) || [];
  }

  /**
   * Get all user's role IDs (Observable)
   * @returns Observable<string[]>
   */
  public getCurrentUserRoles$(): Observable<string[]> {
    return this.authService.currentDbUser$.pipe(map((user: IUser | null) => user?.roles?.map((role) => role.id) || []));
  }
}
